import { RSVPResponse, RSVPStats } from '../types/rsvp';
import { ApiConfig, Environment } from '../config/api';

// Interface for raw API response data
interface RawRSVPResponse {
  id: string;
  guestName: string;
  email?: string;
  phone: string;
  guestCount: number;
  adultCount: number;
  childCount: number;
  weddingCode: string;
  attending: boolean;
  invitationType: 'ceremony-only' | 'ceremony-reception';
  dietaryRestrictions?: string;
  songRequest?: string;
  additionalNotes?: string;
  confirmSingleAttendance?: boolean;
  submittedAt: string | Date;
}

const STORAGE_KEY = 'wedding-rsvp-responses';

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined';

export const saveRSVPResponse = async (response: RSVPResponse): Promise<void> => {
  try {
    // Try API first
    const apiUrl = ApiConfig.getEndpoint('/api/rsvp');
    const apiResponse = await fetch(apiUrl, {
       method: 'POST',
       ...ApiConfig.getRequestConfig(),
       body: JSON.stringify(response),
    });

    if (apiResponse.ok) {
      return;
    }
  } catch (error) {
    // Silently fall back to localStorage
  }

  // Fallback to localStorage
  if (isBrowser) {
    const responses = await getRSVPResponses();
    const newResponse = {
      ...response,
      id: response.id || `rsvp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      submittedAt: response.submittedAt || new Date()
    };
    responses.push(newResponse);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(responses));
  }
};

export const updateRSVPResponse = async (id: string, updatedData: Partial<RSVPResponse>): Promise<void> => {
  try {
    // Try API first
    const apiResponse = await fetch(ApiConfig.getEndpoint('/api/rsvp'), {
      method: 'PUT',
      ...ApiConfig.getRequestConfig(),
      body: JSON.stringify({ id, ...updatedData }),
    });

    if (apiResponse.ok) {
      return;
    }
  } catch (error) {
    // Silently fall back to localStorage
  }

  // Fallback to localStorage
  if (isBrowser) {
    const responses = await getRSVPResponses();
    const index = responses.findIndex(r => r.id === id);
    if (index !== -1) {
      responses[index] = { ...responses[index], ...updatedData };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(responses));
    }
  }
};

export const deleteRSVPResponse = async (id: string): Promise<void> => {
  try {
    // Try API first
    const apiResponse = await fetch(ApiConfig.getEndpoint(`/api/rsvp?id=${encodeURIComponent(id)}`), {
      method: 'DELETE',
      ...ApiConfig.getRequestConfig(),
    });

    if (apiResponse.ok) {
      return;
    }
  } catch (error) {
    // Silently fall back to localStorage
  }

  // Fallback to localStorage
  if (isBrowser) {
    const responses = await getRSVPResponses();
    const filteredResponses = responses.filter(r => r.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredResponses));
  }
};

export const getRSVPResponses = async (): Promise<RSVPResponse[]> => {
  try {
    // Try API first with cache-busting parameter
    const cacheBuster = Date.now();
    const apiResponse = await fetch(ApiConfig.getEndpoint(`/api/rsvp?_t=${cacheBuster}`), {
      method: 'GET',
      cache: 'no-cache',
      ...ApiConfig.getRequestConfig(),
      headers: {
        ...ApiConfig.getRequestConfig().headers,
        'Cache-Control': 'no-cache'
      }
    });

    if (apiResponse.ok) {
      const data = await apiResponse.json();

      if (!Array.isArray(data)) {
        throw new Error('API returned invalid data format');
      }

      const mappedData = data.map((item: any) => ({
        ...item,
        guestName: item.guestName || item.name, // Handle both 'guestName' and 'name' fields
        submittedAt: new Date(item.submittedAt)
      }));

      return mappedData;
    }
  } catch (error) {
    // Silently fall back to localStorage
  }

  // Fallback to localStorage
  if (isBrowser) {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return [];
    }

    try {
      const parsedData = JSON.parse(stored);
       return parsedData.map((item: RawRSVPResponse) => ({
        ...item,
        submittedAt: new Date(item.submittedAt)
      }));
    } catch (error) {
      return [];
    }
  }

  return [];
}

// Synchronous version for backward compatibility
export const getRSVPResponsesSync = (): RSVPResponse[] => {
  if (isBrowser) {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored).map((item: RawRSVPResponse) => ({
      ...item,
      submittedAt: new Date(item.submittedAt)
    })) : [];
  }
  return [];
};

export const getRSVPStats = async (): Promise<RSVPStats> => {
  try {
    // Try API first
    const apiResponse = await fetch(ApiConfig.getEndpoint('/api/stats'));
    if (apiResponse.ok) {
      const stats = await apiResponse.json();
      return stats;
    }
  } catch (error) {
    // Silently fall back to local calculation
  }

  // Fallback to local calculation
  const responses = await getRSVPResponses();
  const attendingResponses = responses.filter(r => r.attending);

  return {
    totalResponses: responses.length,
    attendingGuests: attendingResponses.reduce((sum, r) => sum + r.guestCount, 0)
  };
};

// Synchronous version for backward compatibility
export const getRSVPStatsSync = (): RSVPStats => {
  const responses = getRSVPResponsesSync();
  const attendingResponses = responses.filter(r => r.attending);

  return {
    totalResponses: responses.length,
    attendingGuests: attendingResponses.reduce((sum, r) => sum + r.guestCount, 0)
  };
};

export const exportToCSV = async (): Promise<string> => {
  const responses = await getRSVPResponses();
  const headers = ['Guest Name', 'Phone', 'Attending', 'Guest Count', 'Adults', 'Children', 'Wedding Code', 'Submitted At'];

  const rows = responses.map(r => [
    r.guestName,
    r.phone,
    r.attending ? 'Yes' : 'No',
    r.guestCount.toString(),
    r.adultCount.toString(),
    r.childCount.toString(),
    r.weddingCode,
    new Date(r.submittedAt).toLocaleDateString()
  ]);

  return [headers, ...rows].map(row => row.join(',')).join('\n');
};

// Synchronous version for backward compatibility
export const exportToCSVSync = (): string => {
  const responses = getRSVPResponsesSync();
  const headers = ['Guest Name', 'Phone', 'Attending', 'Guest Count', 'Adults', 'Children', 'Wedding Code', 'Submitted At'];

  const rows = responses.map(r => [
    r.guestName,
    r.phone,
    r.attending ? 'Yes' : 'No',
    r.guestCount.toString(),
    r.adultCount.toString(),
    r.childCount.toString(),
    r.weddingCode,
    new Date(r.submittedAt).toLocaleDateString()
  ]);

  return [headers, ...rows].map(row => row.join(',')).join('\n');
};

// Check if an RSVP already exists for a given phone number or wedding code
export const checkExistingRSVP = async (phone?: string, weddingCode?: string): Promise<RSVPResponse | null> => {
  const responses = await getRSVPResponses();

  // Check by phone number first (more reliable) using normalized comparison
  if (phone) {
    const { normalizePhoneNumber } = await import('./validation');
    const normalizedInputPhone = normalizePhoneNumber(phone);
    const existingByPhone = responses.find(r =>
      normalizePhoneNumber(r.phone) === normalizedInputPhone
    );
    if (existingByPhone) return existingByPhone;
  }

  // Check by wedding code as fallback (case-insensitive)
  if (weddingCode) {
    const existingByCode = responses.find(r =>
      r.weddingCode.toLowerCase() === weddingCode.toLowerCase()
    );
    if (existingByCode) return existingByCode;
  }

  return null;
};

// Store the current RSVP in session storage for persistence across page reloads
export const storeCurrentRSVP = (rsvp: RSVPResponse): void => {
  if (isBrowser) {
    sessionStorage.setItem('currentRSVP', JSON.stringify({
      ...rsvp,
      submittedAt: rsvp.submittedAt.toISOString()
    }));
  }
};

// Get the current RSVP from session storage
export const getCurrentRSVP = (): RSVPResponse | null => {
  if (isBrowser) {
    const stored = sessionStorage.getItem('currentRSVP');
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        ...parsed,
        submittedAt: new Date(parsed.submittedAt)
      };
    }
  }
  return null;
};

// Clear the current RSVP from session storage
export const clearCurrentRSVP = (): void => {
  if (isBrowser) {
    sessionStorage.removeItem('currentRSVP');
  }
};
